import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useTheme } from '@material-ui/core/styles';

import Button from '../../../components/shared/Button';
import DownloadIcon from '../../../assets/icons/DownloadIcon';
import { StatusReportView } from '../../../components/reporting/status-report';
import {
  createStatusReportJob,
  downloadStatusReport,
  getStatusReportJob,
  getStatusReportResult,
} from '../../../actions/statusReportActions';
import { createStatusReportPalette } from '../../../components/reporting/status-report/statusReportColors';

import './status-report-page.scss';

const PERIODS = [
  { label: 'This Month', value: 'month' },
  { label: 'This Quarter', value: 'quarter' },
  { label: 'This Year', value: 'year' },
];

const StatusReportPage = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const currentTenant = useSelector((state) => state?.tenant?.currentTenant || {});
  const tenantColor = currentTenant.colorPrimary;
  const tenantLogo = currentTenant.logo?.downloadUrl
    || currentTenant.documentLogo?.downloadUrl
    || currentTenant.icon?.downloadUrl;
  const palette = useMemo(
    () => createStatusReportPalette(tenantColor, theme.palette.type),
    [tenantColor, theme.palette.type],
  );
  const reportStyle = useMemo(() => ({
    '--status-report-accent': palette.accent,
    '--status-report-accent-readable': palette.accentReadable,
    '--status-report-on-accent': palette.onAccent,
    '--status-report-on-accent-muted': palette.onAccentMuted,
    '--status-report-accent-soft': palette.accentSoft,
    '--status-report-accent-border': palette.accentBorder,
    '--status-report-chart-current': palette.chartCurrent,
    '--status-report-chart-upcoming': palette.chartUpcoming,
    '--status-report-chart-prior': palette.chartPrior,
  }), [palette]);
  const pollRef = useRef({ cancelled: false, timeout: null, jobId: null });
  const [period, setPeriod] = useState('month');
  const [job, setJob] = useState(null);
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [background, setBackground] = useState(false);
  const [error, setError] = useState('');

  const clearPolling = useCallback(() => {
    pollRef.current.cancelled = true;
    if (pollRef.current.timeout) {
      clearTimeout(pollRef.current.timeout);
    }
  }, []);

  useEffect(() => clearPolling, [clearPolling]);

  const pollJob = useCallback(async (jobId, startedAt) => {
    if (pollRef.current.cancelled || pollRef.current.jobId !== jobId) return;

    try {
      const currentJob = await dispatch(getStatusReportJob(jobId));
      if (pollRef.current.cancelled || pollRef.current.jobId !== jobId) return;
      setJob(currentJob);

      if (currentJob.status === 'COMPLETE') {
        const result = await dispatch(getStatusReportResult(jobId));
        if (pollRef.current.cancelled || pollRef.current.jobId !== jobId) return;
        setReport(result);
        setRunning(false);
        setBackground(false);
        return;
      }

      if (currentJob.status === 'FAILED') {
        setError(currentJob.error || 'Unable to generate the report.');
        setRunning(false);
        setBackground(false);
        return;
      }

      const elapsed = Date.now() - startedAt;
      if (elapsed >= 20000) {
        setBackground(true);
        setRunning(false);
      }
      pollRef.current.timeout = setTimeout(
        () => pollJob(jobId, startedAt),
        elapsed >= 20000 ? 5000 : 1000,
      );
    } catch (pollError) {
      if (!pollRef.current.cancelled && pollRef.current.jobId === jobId) {
        setError(pollError.message || 'Unable to check the report status.');
        setRunning(false);
        setBackground(false);
      }
    }
  }, [dispatch]);

  const onRun = async () => {
    clearPolling();
    pollRef.current = { cancelled: false, timeout: null, jobId: null };
    setRunning(true);
    setBackground(false);
    setReport(null);
    setJob(null);
    setError('');

    try {
      const createdJob = await dispatch(createStatusReportJob(period));
      if (pollRef.current.cancelled) return;
      pollRef.current.jobId = createdJob.id;
      setJob(createdJob);
      await pollJob(createdJob.id, Date.now());
    } catch (runError) {
      setError(runError.message || 'Unable to start the report.');
      setRunning(false);
    }
  };

  const onPeriodChange = (nextPeriod) => {
    if (nextPeriod === period) return;
    clearPolling();
    setPeriod(nextPeriod);
    setJob(null);
    setReport(null);
    setBackground(false);
    setError('');
  };

  const onDownload = async () => {
    if (!job?.id || !report) return;
    setDownloading(true);
    setError('');
    try {
      await dispatch(downloadStatusReport(job.id, job.fileName));
    } catch (downloadError) {
      setError(downloadError.message || 'Unable to download the PDF.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="page-container flex-1 status-report-page"
      style={reportStyle}
    >
      <div className="page-title">Status Report</div>

      <div className="status-report-controls">
        <div className="status-report-period" role="tablist" aria-label="Report period">
          {PERIODS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={period === option.value}
              className={period === option.value ? 'is-selected' : ''}
              onClick={() => onPeriodChange(option.value)}
              disabled={running}
            >
              {option.label}
            </button>
          ))}
        </div>
        <Button text="Run" onClick={onRun} loading={running} disabled={running} width="104px" height="48px" />
        <Button
          type="icon"
          onClick={onDownload}
          loading={downloading}
          disabled={!report || downloading}
          width="170px"
          height="48px"
        >
          <span>Download PDF</span>
          <DownloadIcon className="m-l-sm" color="currentColor" />
        </Button>
      </div>

      {!!error && <div className="status-report-message is-error">{error}</div>}
      {background && !report && (
        <div className="status-report-message">
          This report is continuing in the background. The PDF will be emailed to you when it is ready.
        </div>
      )}
      {running && (
        <div className="status-report-loading">
          <CircularProgress size={28} />
          <span>Generating report...</span>
        </div>
      )}
      {!!report && (
        <StatusReportView
          report={report}
          tenantLogo={tenantLogo}
          tenantName={currentTenant.businessName}
          palette={palette}
        />
      )}
    </div>
  );
};

export default StatusReportPage;
